import React, { useMemo } from "react"

import { ChartFigure, type ChartTable } from "@coval/ui/chart-figure"
import { BarChart3, ShieldAlert, Users } from "lucide-react"
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

import { AXIS_TEXT_FILL, DIVIDER_STROKE } from "@/components/charts/chartTheme"
import { PRIMARY_BAR_SERIES_COLOR } from "@/components/charts/seriesPalette"
import { useIsOrgAdmin } from "@/hooks/useIsOrgAdmin"
import { calculatePresetDateRange } from "@/utils/date/dateRange"
import { formatNumber } from "@/utils/formatting/format-number"

import { useDashboardFilters } from "../../contexts/DashboardFiltersContext"
import { useDashboardBatchTransportQuery } from "../../routes/dashboardBatchTransportQuery"
import type { ChartDataPoint } from "../../types/chart"
import { DEFAULT_WIDGET_DATE_RANGE } from "../../types/dateRange"
import {
  getDashboardBatchRows,
  isTransportDashboardQueryLoading,
} from "../../utils/transportDashboardQueries"
import { BaseWidget, type WidgetProps } from "../BaseWidget"
import { ChartWidgetSkeleton } from "../ChartWidgetSkeleton"

// Single-series bar fill: the shared data-viz palette's theme-aware primary
// (blue mid in light, strong in dark).
const DEFAULT_BAR_COLOR = PRIMARY_BAR_SERIES_COLOR
const AXIS_LABEL_COLOR = AXIS_TEXT_FILL
const ASSIGNEE_QUERY_STALE_TIME_MS = 5 * 60 * 1000
const ASSIGNEE_QUERY_GC_TIME_MS = 15 * 60 * 1000

const AssigneeCallVolumeTooltip = ({
  active,
  payload,
  label: tooltipLabel,
}: {
  active?: boolean
  payload?: Array<{ value?: number | string }>
  label?: string
}) => {
  if (!active || !payload || payload.length === 0) {
    return null
  }
  const value = payload[0]?.value
  return (
    <div className="bg-surface-1 border border-coval-border rounded-lg shadow-lg p-3 min-w-[180px]">
      <div className="text-sm font-medium text-coval-text mb-1 border-b border-coval-border pb-2">
        {tooltipLabel}
      </div>
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs text-coval-text-muted">Calls / Day:</span>
        <span className="text-sm font-medium text-coval-text">
          {typeof value === "number" ? formatNumber(value) : value}
        </span>
      </div>
    </div>
  )
}

const AssigneeCallVolumeContent: React.FC<{
  label: string
  onExportDataReady?: WidgetProps["onExportDataReady"]
}> = ({ label, onExportDataReady }) => {
  const isAdmin = useIsOrgAdmin()
  const { state } = useDashboardFilters()
  const {
    dateRange: globalDateRange,
    globalDateRangeOverride,
    isOverrideActive,
  } = state

  const effectiveDateRange = useMemo(() => {
    if (isOverrideActive && globalDateRangeOverride) {
      return globalDateRangeOverride
    }
    if (globalDateRange) {
      return globalDateRange
    }
    return calculatePresetDateRange(DEFAULT_WIDGET_DATE_RANGE.preset!)
  }, [globalDateRange, globalDateRangeOverride, isOverrideActive])

  const assigneeVolumeQueryEnabled = isAdmin && Boolean(effectiveDateRange)
  const assigneeVolumeQuery = useDashboardBatchTransportQuery({
    request: {
      queries: [
        {
          query_id: "assignee_call_volume",
          kind: "queryAssigneeCallVolume",
          start_date: effectiveDateRange?.start?.toISOString(),
          end_date: effectiveDateRange?.end?.toISOString(),
        },
      ],
    },
    options: {
      enabled: assigneeVolumeQueryEnabled,
      staleTime: ASSIGNEE_QUERY_STALE_TIME_MS,
      gcTime: ASSIGNEE_QUERY_GC_TIME_MS,
    },
  })

  const data = useMemo(
    () =>
      getDashboardBatchRows<{
        assignee: string
        calls_per_day: number | string
      }>(assigneeVolumeQuery.data),
    [assigneeVolumeQuery.data],
  )
  const { error } = assigneeVolumeQuery
  const isLoading = isTransportDashboardQueryLoading(
    assigneeVolumeQuery,
    assigneeVolumeQueryEnabled,
  )

  const chartData: ChartDataPoint[] = useMemo(() => {
    if (!data || data.length === 0) {
      return []
    }

    return data.map((row) => ({
      date: row.assignee,
      timestamp: 0,
      bucketTime: "",
      value: Number(row.calls_per_day),
    }))
  }, [data])

  const summary = useMemo(() => {
    const ranked = chartData
      .filter((point) => Number.isFinite(point.value))
      .sort((a, b) => (b.value ?? 0) - (a.value ?? 0))
    const top = ranked[0]
    if (!top) {
      return undefined
    }
    return `${ranked.length} assignee${ranked.length === 1 ? "" : "s"}; highest ${top.date} at ${formatNumber(top.value ?? 0)} calls per day.`
  }, [chartData])

  const table = useMemo(
    (): ChartTable => ({
      columns: [
        { key: "assignee", header: "Assignee" },
        { key: "value", header: "Calls / Day", numeric: true },
      ],
      rows: chartData.map((point, index) => ({
        key: `${index}:${point.date}`,
        cells: {
          assignee: point.date,
          value: Number.isFinite(point.value)
            ? formatNumber(point.value ?? 0)
            : "—",
        },
      })),
    }),
    [chartData],
  )

  const prevExportRef = React.useRef<string>("")
  React.useEffect(() => {
    if (!onExportDataReady) {
      return
    }
    const key = JSON.stringify({
      data: chartData,
      start: effectiveDateRange?.start?.toISOString(),
      end: effectiveDateRange?.end?.toISOString(),
    })
    if (prevExportRef.current === key) {
      return
    }
    prevExportRef.current = key

    if (chartData.length > 0) {
      onExportDataReady({
        widgetType: "chart",
        chartData,
        splitKeys: [],
        displayNameLookups: null,
        dateRange: effectiveDateRange
          ? { start: effectiveDateRange.start, end: effectiveDateRange.end }
          : undefined,
      })
    } else {
      onExportDataReady(null)
    }
  }, [chartData, effectiveDateRange, onExportDataReady])

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-full text-coval-text-muted">
        <div className="text-center">
          <ShieldAlert className="w-8 h-8 mx-auto mb-2" />
          <p>Admin access required</p>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return <ChartWidgetSkeleton />
  }

  if (error && chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-coval-text-muted">
        <div className="text-center">
          <Users className="w-8 h-8 mx-auto mb-2" />
          <p>Error loading assignee data</p>
        </div>
      </div>
    )
  }

  if (!data || chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-coval-text-muted">
        <div className="text-center">
          <BarChart3 className="w-8 h-8 mx-auto mb-2" />
          <p>No assignee call data available</p>
        </div>
      </div>
    )
  }

  return (
    <ChartFigure
      label={label}
      summary={summary}
      table={table}
      className="h-full w-full"
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          role="img"
          aria-label={label}
          data={chartData}
          maxBarSize={32}
          margin={{ top: 2, right: 12, left: 20, bottom: 20 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            horizontal
            vertical={false}
            stroke={DIVIDER_STROKE}
            strokeWidth={1}
          />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11 }}
            tickLine={false}
            axisLine={{ stroke: DIVIDER_STROKE, strokeWidth: 1 }}
            label={{
              value: "Assignee",
              position: "insideBottom",
              offset: -5,
              style: { fontSize: 11, fill: AXIS_LABEL_COLOR },
            }}
          />
          <YAxis
            tick={{ fontSize: 11 }}
            tickLine={false}
            axisLine={{ stroke: DIVIDER_STROKE, strokeWidth: 1 }}
            width={45}
            label={{
              value: "Calls / Day",
              angle: -90,
              position: "insideLeft",
              offset: 0,
              style: {
                fontSize: 11,
                fill: AXIS_LABEL_COLOR,
                textAnchor: "middle",
              },
            }}
          />
          <Tooltip content={AssigneeCallVolumeTooltip} />
          <Bar dataKey="value" fill={DEFAULT_BAR_COLOR} />
        </BarChart>
      </ResponsiveContainer>
    </ChartFigure>
  )
}

export class AssigneeCallVolumeWidget extends BaseWidget {
  type = "assignee_call_volume"
  title = "Assignee Call Volume"
  description = "Bar chart of calls/day per assignee across all projects"
  icon = (<Users className="w-5 h-5" />)
  disabled = false

  renderWidget(props: WidgetProps): React.ReactNode {
    return (
      <div className="w-full h-full select-none">
        <AssigneeCallVolumeContent
          label={
            (props.widget.config as { title?: string } | undefined)?.title ||
            props.widget.title ||
            this.title
          }
          onExportDataReady={props.onExportDataReady}
        />
      </div>
    )
  }

  getDefaultConfig() {
    return {
      title: "Assignee Call Volume",
      description: "Calls per day by assignee across all projects",
    }
  }
}
